import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";
import { adminGetPosts, adminCreatePost, adminUpdatePost, adminDeletePost, formatApiError } from "@/lib/api";

const EMPTY = {
  slug: "",
  title: "",
  category: "Engineering",
  excerpt: "",
  content: "",
  author: "Admin",
  author_role: "Editor",
  read_time: "5 min read",
  image: "https://images.pexels.com/photos/29506609/pexels-photo-29506609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  published_at: new Date().toISOString().slice(0, 10),
  tags: "",
};

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post being edited or 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminGetPosts();
      setPosts(data || []);
    } catch (e) {
      toast.error("Could not load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (params.get("new") === "1") {
      openNew();
      params.delete("new");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const openEdit = (p) => {
    setEditing(p.slug);
    setForm({
      slug: p.slug,
      title: p.title,
      category: p.category,
      excerpt: p.excerpt,
      content: p.content,
      author: p.author,
      author_role: p.author_role || "Editor",
      read_time: p.read_time || "5 min read",
      image: p.image,
      published_at: p.published_at,
      tags: (p.tags || []).join(", "),
    });
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const onTitleChange = (e) => {
    const title = e.target.value;
    setForm((f) => ({ ...f, title, slug: editing === "new" ? slugify(title) : f.slug }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        await adminCreatePost(payload);
        toast.success("Post created");
      } else {
        const { slug, ...patch } = payload;
        await adminUpdatePost(editing, patch);
        toast.success("Post updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.message, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await adminDeletePost(p.slug);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Content ]</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl tracking-tight">Blog posts.</h1>
          <p className="mt-2 text-zinc-400">Create, edit, and delete published articles.</p>
        </div>
        <button
          data-testid="admin-blog-new"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-5 py-2.5 text-sm hover:brightness-110 transition self-start"
        >
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>

      <div className="mt-10 rounded-2xl glass border-white/[0.08] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-zinc-500 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">No posts yet.</div>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {posts.map((p) => (
              <li key={p.id} data-testid={`admin-post-row-${p.slug}`} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <img src={p.image} alt="" className="w-full md:w-24 h-24 md:h-16 rounded-lg object-cover border border-white/10" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest text-zinc-300 px-2.5 py-0.5">
                      {p.category}
                    </span>
                    <span className="text-xs text-zinc-500">{p.published_at}</span>
                  </div>
                  <div className="font-cabinet font-medium text-base truncate">{p.title}</div>
                  <div className="text-xs text-zinc-500 truncate font-mono">/{p.slug}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`admin-edit-${p.slug}`}
                    onClick={() => openEdit(p)}
                    className="inline-flex items-center gap-1 rounded-full bg-white/[0.04] border border-white/10 px-3 py-1.5 text-xs hover:bg-white/[0.08]"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button
                    data-testid={`admin-delete-${p.slug}`}
                    onClick={() => remove(p)}
                    className="inline-flex items-center gap-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-1.5 text-xs hover:bg-red-500/20"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={close}>
          <div
            data-testid="admin-blog-editor"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl glass-strong border border-white/10 p-6 md:p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl tracking-tight">
                {editing === "new" ? "New blog post" : "Edit post"}
              </h2>
              <button onClick={close} aria-label="Close" className="h-8 w-8 grid place-items-center rounded-full bg-white/5 border border-white/10">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={save} className="space-y-4">
              <Field label="Title">
                <input data-testid="editor-title" required value={form.title} onChange={onTitleChange} className={inputCls} placeholder="Article title" />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Slug">
                  <input data-testid="editor-slug" required disabled={editing !== "new"} value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className={`${inputCls} font-mono text-xs disabled:opacity-60`} />
                </Field>
                <Field label="Category">
                  <input data-testid="editor-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} placeholder="DevOps, AI/ML, Design…" />
                </Field>
              </div>
              <Field label="Excerpt">
                <textarea data-testid="editor-excerpt" required rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Content">
                <textarea data-testid="editor-content" required rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputCls} font-mono text-xs`} />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Author">
                  <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} required />
                </Field>
                <Field label="Author role">
                  <input value={form.author_role} onChange={(e) => setForm({ ...form, author_role: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Read time">
                  <input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} className={inputCls} placeholder="6 min read" />
                </Field>
                <Field label="Published date">
                  <input type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} className={inputCls} required />
                </Field>
              </div>
              <Field label="Image URL">
                <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} required />
              </Field>
              <Field label="Tags (comma-separated)">
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} placeholder="React, Performance" />
              </Field>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={close} className="text-sm text-zinc-400 hover:text-foreground px-3 py-2">Cancel</button>
                <button
                  data-testid="editor-save"
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-5 py-2.5 text-sm hover:brightness-110 transition disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save post"} <Save className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-nexus-cyan/60 focus:bg-white/[0.05] transition placeholder:text-zinc-600";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 inline-block">{label}</span>
      {children}
    </label>
  );
}
